/**
 * ByodKpiTrendIndicator
 *
 * Trend line for a KPI tile — a directional arrow + percentage change vs the
 * comparison period, rendered under the KPI value (KPIBox bottomContent) when
 * the KPI_TREND_ENABLED flag is on. up → success/green, down → danger/red,
 * neutral/unknown → secondary.
 *
 * Gated on `percentage`: when trend_percentage is null or empty (e.g. no
 * comparison data because no date filters were sent), or a non-finite number,
 * renders NOTHING — the KPI shows just the value, no badge.
 */

import * as React from 'react';

import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {BodySmall} from '@spaced-out/ui-design-system/lib/components/Text';

import type {TrendDirection} from 'src/types/byod';

import css from './byod-kpi-trend-indicator.css';


export type ByodKpiTrendIndicatorProps = {
  direction: TrendDirection;
  percentage: number | string | null;
};

const DIRECTION_CONFIG: Record<
  'up' | 'down' | 'neutral',
  {icon: string; color: 'success' | 'danger' | 'secondary'}
> = {
  up: {icon: 'arrow-trend-up', color: 'success'},
  down: {icon: 'arrow-trend-down', color: 'danger'},
  neutral: {icon: 'minus', color: 'secondary'},
};

function formatPercentage(value: number | string | null): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    // Guard against NaN / Infinity from a bad upstream computation — render
    // nothing rather than a literal "NaN%" badge. Show the magnitude only:
    // the direction (arrow + color) already conveys sign, so a signed value
    // would render a redundant "-8%" next to a down arrow.
    return Number.isFinite(value) ? `${Math.abs(value)}%` : null;
  }
  // String — backend may have already formatted it (e.g. "12.5%").
  return value;
}

export function ByodKpiTrendIndicator({
  direction,
  percentage,
}: ByodKpiTrendIndicatorProps) {
  const pct = formatPercentage(percentage);

  // No percentage → no badge; the KPI shows just its value.
  if (pct == null) {
    return null;
  }

  // `direction` comes off the wire and isn't validated at the boundary, so an
  // unexpected value would index to `undefined` and crash on `config.icon`.
  // Fall back to neutral.
  const config =
    DIRECTION_CONFIG[direction as 'up' | 'down' | 'neutral'] ??
    DIRECTION_CONFIG.neutral;

  return (
    <div className={css.trend}>
      <Icon name={config.icon} size="small" color={config.color} />
      <BodySmall color={config.color}>{pct}</BodySmall>
    </div>
  );
}
